/**
 * What a data source has been told, and what it still needs to be asked.
 *
 * All of it pure: these are questions about the configuration a widget holds,
 * and the answers decide what the settings panel offers and whether the widget
 * is worth dispatching at all.
 */
import type {
  DashboardDataSource,
  Field,
  IntegrationDatabase,
  IntegrationView,
} from '@/api/types'
import {
  aggregationsForField,
  isAggregationCompatible,
  isAggregationTypeName,
  type AggregationTypeName,
} from '@/features/database/aggregations'

/**
 * Whether the server can answer this data source at all.
 *
 * The two aggregations refuse to dispatch until every part of the question has
 * been chosen -- no table, no field, or no aggregation and the answer is a 400.
 * Asking anyway would put a red badge on a widget somebody is halfway through
 * making, and spend a request on every board load to find out what is already
 * written down.
 *
 * Any other service type answers `false`: a dashboard makes only these two, so
 * a third is something this build cannot draw and has nothing to fix.
 */
export function isConfigured(source: DashboardDataSource): boolean {
  if (source.type === 'local_baserow_aggregate_rows') {
    return (
      typeof source.table_id === 'number' &&
      typeof source.field_id === 'number' &&
      (source.aggregation_type ?? '') !== ''
    )
  }
  if (source.type === 'local_baserow_category_aggregate_rows') {
    return (
      typeof source.table_id === 'number' &&
      typeof source.group_by_field_id === 'number' &&
      typeof source.aggregation_field_id === 'number' &&
      (source.aggregation_type ?? '') !== ''
    )
  }
  return false
}

/**
 * The views of one table.
 *
 * Kept per database rather than per table by the endpoint, so the table's own
 * are found by filtering. A view is optional on a data source -- without one the
 * aggregation runs over the whole table -- and only views that can filter or
 * sort are in the list, because those are the only ones that change the answer.
 */
export function viewsFor(
  databases: readonly IntegrationDatabase[],
  tableId: number,
): IntegrationView[] {
  return databases
    .flatMap((database) => database.views)
    .filter((view) => view.table_id === tableId)
}

/**
 * Which aggregation to ask for alongside a newly chosen field.
 *
 * **Not a nicety: the backend refuses the pair.** Sending a field the held
 * aggregation cannot be computed over is a 400, and sending a field with no
 * aggregation at all quietly clears the one that was there. So a change of
 * field carries a change of aggregation whenever the old one no longer applies,
 * and keeps it when it does -- switching from one number column to another
 * should not silently turn a sum into a count.
 */
export function aggregationFor(
  field: Field | undefined,
  held: string | undefined,
): AggregationTypeName | '' {
  if (field === undefined) return ''
  if (isAggregationTypeName(held) && isAggregationCompatible(held, field)) {
    return held
  }
  return aggregationsForField(field)[0] ?? ''
}
